// ============================================================
// The GM's horde tools (plan map-display, Track B): split one horde in two,
// or break it. Neither is a rule - the engine has none for either - they are
// the GM moving things the way the table decides (user, 2026-10-07).
//
//   - SPLIT: the bodies part into two hordes, each with its share of the
//     magnitude (the larger half stays). The maxima divide in the SAME
//     PROPORTION, so each half is exactly as hurt as the whole was - a whole
//     horde makes two whole halves, a battered one two battered ones.
//   - BREAK: the horde runs and is gone - the caller marks it `broken` (its
//     plate flickers out, plan design-gaps P7), says so, and takes it off the
//     table once the flicker has played.
//
// Pure. Zero React dependency.
// ============================================================
import { isCrowd } from '../bodyShape';
import deepClone from '../../utils/deepClone';
import type { Target } from '../../types';

/** The horde split in two, or null when there is nothing to split (one body, or not a horde). */
export function splitHorde(target: Target): { kept: Target; split: Target } | null {
  const h = target.horde;
  // A horde already fleeing (broken) splits no more.
  if (!isCrowd(target) || !h || h.magnitude < 2 || h.broken) return null;
  const share = Math.floor(h.magnitude / 2);
  const kept = h.magnitude - share;
  const max = Math.max(h.maxMagnitude, h.magnitude);
  const splitMax = Math.max(share, Math.round((max * share) / h.magnitude));
  return {
    kept: { ...target, horde: { ...h, magnitude: kept, maxMagnitude: Math.max(kept, max - splitMax) } },
    split: { ...deepClone(target), horde: { ...h, magnitude: share, maxMagnitude: splitMax } },
  };
}

/** The line a split speaks. */
export const splitLine = (name: string, kept: number, split: number) => `${name} splits in two: ${kept} and ${split}.`;

/** The horde marked broken, fleeing (P7: its plate flickers out); a target that is no horde, as it is. */
export function brokenHorde(target: Target): Target {
  if (!isCrowd(target) || !target.horde) return target;
  return { ...target, horde: { ...target.horde, broken: true } };
}

/** The line a break speaks. */
export const brokenLine = (name: string) => `${name} breaks and flees the field.`;
